import { useState, useMemo } from 'react';
import { ContactSyncModel } from '../../../../types';
import SimpleDrawer, {
  SimpleDrawerAction,
} from '../../../shared/simple-drawer-new';
import SharedReviewLayoutMainCard, {
  MainSectionItem,
} from '../../../shared/shared-review-layout-main-card';
import SharedReviewLayoutNew, {
  ReviewSection,
} from '../../../shared/shared-review-layout-new';
import SharedCard from '../../../shared/shared-card';
import { useUserSettings } from '../../../utils/use-user-settings';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import ContactSyncRunLogsGrid from './contact-sync-run-logs-grid';
import { TargetType } from '../../../shared/shared-review-layout-item';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useTheme } from '@mui/material/styles';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Person from '@mui/icons-material/Person';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Update from '@mui/icons-material/Update';

interface ContactSyncDetailsDrawerProps {
  open: boolean;
  handleClose: () => void;
  selectedContactSync: ContactSyncModel | null;
  onRun?: (contactSync: ContactSyncModel) => void;
  onEdit?: (contactSync: ContactSyncModel) => void;
  onDelete?: (contactSync: ContactSyncModel) => void;
  onDeleteLabels?: (contactSync: ContactSyncModel) => void;
  onViewUserStatus?: (contactSync: ContactSyncModel) => void;
}

export default function ContactSyncDetailsDrawer({
  open,
  handleClose,
  selectedContactSync,
  onRun,
  onEdit,
  onDelete,
  onDeleteLabels,
  onViewUserStatus,
}: Readonly<ContactSyncDetailsDrawerProps>) {
  const { directoryRoles } = useUserSettings();
  const theme = useTheme();

  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const canEdit = isAbleToSeeUIElement(
    directoryRoles || [],
    DirectoryRoles.EditContactSync.value
  );

  const canDelete = isAbleToSeeUIElement(
    directoryRoles || [],
    DirectoryRoles.DeleteContactSync.value
  );

  const canRemoveLabel = isAbleToSeeUIElement(
    directoryRoles || [],
    DirectoryRoles.RemoveContactSyncLabel.value
  );

  const actionsMenu = useMemo(() => {
    if (!selectedContactSync) return undefined;

    const menu: SimpleDrawerAction[] = [
      {
        label: 'Run',
        icon: <PlayArrow fontSize="small" />,
        disabled: false,
        onClick: () => {
          handleClose();
          onRun?.(selectedContactSync);
        },
      },
      {
        label: 'Edit',
        icon: <Edit fontSize="small" />,
        disabled: !canEdit,
        disabledReason: !canEdit
          ? Messages.Errors.Permission.Acting(
              DirectoryRoles.EditContactSync.name
            )
          : undefined,
        onClick: () => {
          handleClose();
          onEdit?.(selectedContactSync);
        },
      },
    ];

    menu.push({
      label: 'Delete',
      icon: <Delete fontSize="small" />,
      disabled: !canDelete,
      disabledReason: !canDelete
        ? Messages.Errors.Permission.Acting(
            DirectoryRoles.DeleteContactSync.name
          )
        : undefined,
      onClick: () => {
        handleClose();
        onDelete?.(selectedContactSync);
      },
    });

    if (
      Array.isArray(selectedContactSync.targets) &&
      selectedContactSync.targets.length > 0
    ) {
      menu.push({
        label: 'Delete Labels',
        icon: <Close fontSize="small" />,
        disabled: !canRemoveLabel,
        disabledReason: !canRemoveLabel
          ? Messages.Errors.Permission.Acting(
              DirectoryRoles.RemoveContactSyncLabel.name
            )
          : undefined,
        onClick: () => {
          handleClose();
          onDeleteLabels?.(selectedContactSync);
        },
      });
    }

    menu.push({
      label: 'View User Status',
      icon: <Person fontSize="small" />,
      disabled: false,
      onClick: () => {
        handleClose();
        onViewUserStatus?.(selectedContactSync);
      },
    });

    return menu;
  }, [
    selectedContactSync,
    canEdit,
    canDelete,
    canRemoveLabel,
    handleClose,
    onRun,
    onEdit,
    onDelete,
    onDeleteLabels,
    onViewUserStatus,
  ]);

  const formattedTimes = useMemo(() => {
    if (!selectedContactSync?.times) return [];
    return selectedContactSync.times.map((time) => {
      const parsed = parseInt(time);
      if (isNaN(parsed)) return time;
      if (parsed === 0) return '12 AM';
      if (parsed < 12) return `${parsed} AM`;
      if (parsed === 12) return '12 PM';
      return `${parsed - 12} PM`;
    });
  }, [selectedContactSync?.times]);

  const targetsValue = useMemo(() => {
    if (!selectedContactSync) return null;
    return selectedContactSync.targetType === 'All_Users' ||
      selectedContactSync.targetType === 'All_Groups' ||
      selectedContactSync.allSource
      ? 'All'
      : selectedContactSync.targets && selectedContactSync.targets.length > 0
      ? selectedContactSync.targets.map((target) => {
          return {
            email: target,
            type: (selectedContactSync.targetType as TargetType) || 'Unknown',
          };
        })
      : 'No Targets';
  }, [selectedContactSync]);

  const sections: ReviewSection[] = useMemo(() => {
    if (!selectedContactSync) return [];

    return [
      {
        title: 'Basic Information',
        leftItems: [
          {
            label: 'Sync Type:',
            value:
              selectedContactSync.syncType === 'directory'
                ? 'Directory Sync'
                : selectedContactSync.syncType === 'contact'
                ? 'Contact Sync'
                : 'Bi-Directional Sync',
          },
          {
            label: 'Sync User:',
            value: selectedContactSync.sourceUser
              ? [
                  {
                    email: selectedContactSync.sourceUser,
                    type: 'User' as const,
                  },
                ]
              : 'N/A',
            variant: 'chip',
            isVisible: !!selectedContactSync.sourceUser,
          },
          {
            label: 'Source Label:',
            value: selectedContactSync.groupTitle || 'N/A',
            isVisible: !!selectedContactSync.groupTitle,
          },
          {
            label: 'Target Label:',
            value: selectedContactSync.targetGroupName || 'N/A',
          },
        ],
      },
      {
        title: 'Targets',
        leftItems: [
          {
            label: 'Target Type:',
            value: selectedContactSync.targetType || 'N/A',
          },
          {
            label: 'Targets:',
            value: targetsValue,
            variant: Array.isArray(targetsValue) ? 'chip' : 'string',
            flex: Array.isArray(targetsValue) && targetsValue.length > 1,
          },
        ],
      },
      {
        title: 'Schedule',
        leftItems: [
          {
            label: 'Times:',
            value: formattedTimes.join(', '),
            isVisible:
              selectedContactSync.scheduled && formattedTimes.length > 0,
          },
          {
            label: 'Dates:',
            value: (selectedContactSync.dates || []).join(', '),
            isVisible:
              selectedContactSync.scheduled &&
              !!selectedContactSync.dates &&
              selectedContactSync.dates.length > 0,
          },
          {
            label: 'Expiration Date:',
            value: selectedContactSync.scheduleEndDate
              ? new Date(
                  selectedContactSync.scheduleEndDate
                ).toLocaleDateString()
              : 'N/A',
            isVisible:
              selectedContactSync.scheduled &&
              !!selectedContactSync.scheduleEndDate,
          },
        ],
      },
    ];
  }, [selectedContactSync, targetsValue, formattedTimes]);

  const mainSection: MainSectionItem | null = useMemo(() => {
    if (!selectedContactSync) return null;
    return {
      mainTitle: selectedContactSync.name,
      description:
        selectedContactSync.description || 'No description provided.',
      fields: [
        {
          label: 'Last Run',
          value: selectedContactSync.lastRunTime
            ? new Date(selectedContactSync.lastRunTime).toLocaleString()
            : 'N/A',
          icon: Update,
        },
        {
          label: 'Created Date',
          value: selectedContactSync.createdTime
            ? new Date(selectedContactSync.createdTime).toLocaleString()
            : 'N/A',
          icon: Check,
        },
      ],
    };
  }, [selectedContactSync]);

  return (
    <SimpleDrawer
      open={open}
      title="Contact Sync Details"
      onClose={handleClose}
      actionsMenu={actionsMenu}
    >
      {selectedContactSync && mainSection && (
        <>
          <SharedReviewLayoutMainCard section={mainSection} isDrawer />

          <SharedCard>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                px: 2,
                backgroundColor: theme.palette.semantic.surface.bg1,
              }}
            >
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="contact sync details tabs"
              >
                <Tab label="Overview" />
                <Tab label="Logs" />
              </Tabs>
            </Box>

            <Box
              sx={{
                pt: 2,
                pb: 4,
                px: 2,
                backgroundColor: theme.palette.semantic.surface.bg1,
              }}
            >
              {tabValue === 0 && (
                <SharedReviewLayoutNew
                  leftSections={sections}
                  isDrawer={true}
                  variant="light"
                />
              )}
              {tabValue === 1 && (
                <Box sx={{ height: 560 }}>
                  <ContactSyncRunLogsGrid
                    selectedContactSync={selectedContactSync}
                  />
                </Box>
              )}
            </Box>
          </SharedCard>
        </>
      )}
    </SimpleDrawer>
  );
}
